import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Tooltip,
  Typography,
  List,
  ListSubheader,
  ListItemButton,
  Chip,
  Box,
  Drawer,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { humanizeDate, typography } from '../../utils';
import {
  useGetAllNotificationQuery,
  useDeleteNotificationMutation,
} from '../../services/slices/user/userSlice';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import { useTranslation } from '../../hooks';
import { useNavigate } from 'react-router-dom';

const Satus = () => {
  const statusInfo = { label: '●', chipVariant: 'success' };
  return (
    <Chip
      sx={{
        backgroundColor: 'transparent',
        fontSize: '1.5rem',
      }}
      variant={statusInfo.chipVariant}
      label={statusInfo.label}
    />
  );
};

const RenderNotification = ({ notifications = [] }) => {
  const [showAll, setShowAll] = React.useState(false);
  const [expandedId, setExpandedId] = React.useState(null);
  const [DeleteNotification] = useDeleteNotificationMutation();

  const handleNotificationClick = async (notification) => {
    setExpandedId((prev) => (prev === notification?._id ? null : notification?._id));

    if (!notification.isRead) {
      await DeleteNotification({
        id: notification?._id,
        isRead: true,
      });
    }
  };

  return (
    <>
      {notifications.slice(0, showAll ? notifications.length : 3).map((notification) => {
        const isUnread = !notification.isRead;
        const isExpanded = expandedId === notification?._id;

        return (
          <ListItemButton
            key={notification?._id}
            onClick={() => handleNotificationClick(notification)}
            sx={{
              mb: 1,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              flexDirection: 'column',
              alignItems: 'flex-start',
              backgroundColor: isUnread ? '#EEF4FF' : 'transparent',
              borderLeft: isUnread ? '4px solid #3B82F6' : '4px solid transparent',
              transition: 'all 0.25s ease',
              position: 'relative',

              '&:hover': {
                backgroundColor: isUnread ? '#E0EBFF' : '#F5F7FA',
              },
              zIndex: 5,
            }}
          >
            {/* HEADER: TITLE & MESSAGE */}
            <Box width="100%" display="flex">
              <Box flex={1}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: isUnread ? 600 : 500,
                    color: '#0F172A',
                  }}
                >
                  {typography(notification.title)}
                </Typography>

                <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                  {isExpanded
                    ? typography(notification.message)
                    : typography(`${notification.message.slice(0, 60)}...`)}
                </Typography>
              </Box>

              {/* RIGHT META: SHORT DATE & UNREAD DOT */}
              <Box
                ml={2}
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                sx={{ position: 'relative', minWidth: 50 }}
              >
                {isUnread && (
                  <Box mt={1} width={8} height={8} borderRadius="50%" bgcolor="#2563EB" />
                )}

                {/* SHORT DATE */}
                <Typography
                  variant="caption"
                  sx={{
                    color: '#94A3B8',
                  }}
                >
                  {humanizeDate(notification.createdAt)}
                </Typography>
              </Box>
            </Box>

            {/* EXPANDED CONTENT */}
            {isExpanded && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: '#475569',
                  lineHeight: 1.6,
                }}
              >
                {typography(notification.message)}
              </Typography>
            )}

            {/* HOVER OVERLAY: CENTERED DATE/TIME ONLY IF NOT EXPANDED */}
            {!isExpanded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.25s ease',
                  pointerEvents: 'none',
                  bgcolor: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: 'blur(6px)',
                  borderRadius: 2,
                  '.MuiListItemButton-root:hover &': {
                    opacity: 1,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: '#0F172A',
                    textAlign: 'center',
                  }}
                >
                  {new Date(notification.createdAt).toLocaleString()}
                </Typography>
              </Box>
            )}

            {/* FULL DATE/TIME AT BOTTOM-RIGHT IF EXPANDED */}
            {isExpanded && (
              <Box
                sx={{
                  mt: '5px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 200,
                    color: '#0F172A',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {new Date(notification.createdAt).toLocaleString()}
                </Typography>
              </Box>
            )}
          </ListItemButton>
        );
      })}

      {/* SEE MORE BUTTON */}
      {notifications.length > 3 && !showAll && (
        <ListItemButton
          onClick={() => setShowAll(true)}
          sx={{
            justifyContent: 'center',
            color: '#2563EB',
            fontWeight: 600,
            mt: 1,
            '&:hover': { backgroundColor: '#EEF4FF' },
          }}
        >
          <MoreHorizIcon sx={{ mr: 1 }} />
          See More
        </ListItemButton>
      )}
    </>
  );
};

const NotificationBell = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  // const [type, setType] = useState('Latest');

  // useGetAllNotificationQuery('LATEST');
  const {
    data: notifications = [],
    isLoading,
    isError,
  } = useGetAllNotificationQuery('LATEST', { pollingInterval: 10_000 });

  const [open, setOpen] = React.useState(false);
  const handleClose = () => setOpen(false);

  const hadleAllNotification = () => {
    navigate('/notifications');
    setOpen(false);
  };

  const unreadCount = notifications?.filter((n) => !n.isRead).length;

  const handleOpen = () => {
    setOpen(true);
    // setType('LATEST');
  };

  return (
    <>
      <Tooltip
        sx={{ mx: '10px' }}
        title={`You have ${unreadCount} ${unreadCount > 1 ? 'notifications' : 'notification'}`}
      >
        <IconButton size="medium" color="inherit" onClick={handleOpen}>
          <Badge
            overlap="circular"
            badgeContent={unreadCount}
            // variant="dot"
            color="primary"
          >
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Stack width={{ xs: '90svw', md: '550px' }} height="100%">
          <Stack direction="row" justifyContent="space-between" alignItems="center" px={2} py={1}>
            <Typography variant="h6" fontSize="18px">
              {t('Alerts & Notifications')}
            </Typography>

            <Stack direction="row" gap="16px">
              <IconButton
                size="small"
                aria-label="close"
                onClick={handleClose}
                sx={{
                  borderRadius: '4px',
                  padding: '2px',
                  background: (theme) => theme.palette.grey[200],
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Stack>
          </Stack>

          <Divider />

          {/* {notifications.length === 0 ? <NoRowsOverlay /> : false} */}

          {/* <Stack direction="row" justifyContent="space-between" p="16px 24px">
            <Typography fontSize="14px">{t('Latest')}</Typography>
            <Button sx={{ p: 0 }} variant="neutral" onClick={markAllAsRead}>
              {t('Mark all as read')}
            </Button>
          </Stack> */}

          <List
            sx={{ maxWidth: '500px', bgcolor: 'background.paper' }}
            component="nav"
            aria-labelledby="nested-list-subheader"
            subheader={
              <ListSubheader
                component="div"
                sx={{
                  fontSize: '1.2rem',
                  backgroundColor: 'white',
                  color: 'black',
                  borderBottom: '1px solid #EBEBEB',
                }}
              >
                Notifications
              </ListSubheader>
            }
          >
            <RenderNotification notifications={notifications || []} />
          </List>

          <Stack
            mt="auto"
            direction="row"
            justifyContent="center"
            alignItems="center"
            px={2}
            py={1}
            gap="8px"
          >
            <Button
              variant="neutral"
              onClick={hadleAllNotification}
              sx={{ p: 0 }}
              endIcon={<KeyboardDoubleArrowDownIcon fontSize="8px" />}
            >
              {t('ALL Notifications')}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};
export default NotificationBell;
